import { SURFACES, type PersonUsage } from '@/billing/api';
import { formatCopy } from '@/i18n/ns';
import { WorkspaceIcon } from '@/workspace/icons';
import { credits, type Formatters } from './format';
import type { UsageCopy } from './copy';
import { Avatar, limitState, roleLabel } from './people-list';

/** The strip above the cards while one person is selected. */
export function PersonSummary({
  person,
  color,
  share,
  you,
  canManage,
  copy,
  f,
  onEdit,
  onClear,
}: {
  person: PersonUsage;
  color: string;
  /** Null in a member's own view, where the company total is not shown. */
  share: number | null;
  you: boolean;
  canManage: boolean;
  copy: UsageCopy;
  f: Formatters;
  onEdit?: () => void;
  onClear?: () => void;
}) {
  const total = credits(person.creditMicros);
  const top = SURFACES.reduce((best, surface) =>
    person.surfaces[surface] > person.surfaces[best] ? surface : best,
  );
  const { used, limit, pct, warn } = limitState(person);
  const role = roleLabel(copy, person.role);
  return (
    <section className="cu-focus" aria-label={person.name}>
      <div className="cu-focus-who">
        <Avatar name={person.name} color={color} size="lg" />
        <div>
          <div className="cu-focus-name">{person.name}</div>
          <div className="cu-focus-role">
            {you ? formatCopy(copy.summaryRoleYou, { role }) : role}
          </div>
        </div>
      </div>
      <div className="cu-focus-stats">
        <div className="cu-stat">
          <div className="cu-stat-label">{copy.summaryCredits}</div>
          <div className="cu-stat-value cu-num">{f.int(total)}</div>
        </div>
        {share !== null && (
          <div className="cu-stat">
            <div className="cu-stat-label">{copy.summaryShare}</div>
            <div className="cu-stat-value">{f.percent(share)}</div>
          </div>
        )}
        <div className="cu-stat">
          <div className="cu-stat-label">{copy.summaryTop}</div>
          <div className="cu-stat-value">
            {copy[top]}
            {total > 0 && (
              <small>
                {formatCopy(you ? copy.summaryTopShareSelf : copy.summaryTopShare, {
                  percent: f.percent((credits(person.surfaces[top]) / total) * 100),
                })}
              </small>
            )}
          </div>
        </div>
        <div className="cu-stat">
          <div className="cu-stat-label">{copy.usedLimit}</div>
          <div className="cu-stat-value">
            <span className="cu-num">{f.int(used)}</span>
            <small>
              {limit === null ? copy.noLimit : formatCopy(copy.ofLimit, { limit: f.int(limit) })}
            </small>
            {warn && <span className="cu-badge">{f.percent(pct)}</span>}
          </div>
        </div>
      </div>
      {(onClear || (canManage && onEdit)) && (
        <div className="cu-focus-actions">
          {canManage && onEdit && (
            <button type="button" className="cu-btn" onClick={onEdit}>
              {limit === null ? copy.setLimit : copy.editLimit}
            </button>
          )}
          {onClear && (
            <button type="button" className="cu-btn is-quiet" onClick={onClear}>
              <WorkspaceIcon name="close" size={14} />
              {copy.showAllUsers}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
